import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useToast } from "@/app/hooks/use-toast";
import { useAuth } from "@/app/hooks/useAuth";
import { User, LogOut, ArrowLeft, ArrowRight, Mail, MailCheck, Lock, ShieldAlert, Clock } from "lucide-react";
import { CandidateRegistrationRequest, isPendingVerification, AuthErrorBody } from "@/services/api/types";
import { authApi } from "@/services/endpoints/auth";
import { getUserRole } from "@/utils/auth";
import { useCountdown } from "@/app/hooks/useCountdown";
import { getAccountLock, formatLockCountdown } from "@/utils/lockout";
import { AuthLayout } from "@/components/auth/AuthLayout";
import { AuthInput } from "@/components/auth/AuthInput";
import { AuthSubmitButton } from "@/components/auth/AuthSubmitButton";
import { surfaceStyle } from "@/components/auth/authStyles";

export default function Auth() {
  const [activeTab, setActiveTab] = useState<"login" | "signup" | "forgot" | "verify">("login");
  const [loading, setLoading] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const [verifyEmail, setVerifyEmail] = useState("");
  const { toast } = useToast();
  const { login, logout, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const redirectTo = searchParams.get('redirect') || '/jobs';
  // Set by the email-verification page (`/auth?email=…`) so a newly verified
  // candidate only has to type the password (T100826078).
  const prefilledEmail = searchParams.get("email")?.trim() ?? "";

  const [loginData, setLoginData] = useState({
    email: prefilledEmail,
    password: ""
  });

  const [signupData, setSignupData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: ""
  });

  // Failed-login lockout state: drives a live countdown and disables the
  // sign-in button until the lockout window elapses.
  const [lockUntil, setLockUntil] = useState<Date | null>(null);
  const lockSecondsLeft = useCountdown(lockUntil);
  const isLocked = lockUntil !== null && lockSecondsLeft > 0;

  // Re-enable the login form once the lockout window elapses.
  useEffect(() => {
    if (lockUntil && lockSecondsLeft === 0) {
      setLockUntil(null);
    }
  }, [lockUntil, lockSecondsLeft]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLocked) return;
    setLoading(true);

    try {
      const loginResponse = await authApi.login(loginData.email, loginData.password);
      const { data } = loginResponse;

      const role = getUserRole(data.tokens.accessToken) || data.user.role;

      login({
        id: data.user.id || "",
        email: data.user.email || loginData.email,
        firstName: data.user.firstName || "",
        lastName: data.user.lastName || "",
        role: role || undefined
      }, data.tokens.accessToken, data.tokens.refreshToken);

      toast({
        title: "Welcome back",
        description: "You've been successfully signed in.",
      });

      navigate(redirectTo);
    } catch (error: any) {
      const status: number | undefined = error?.response?.status;
      const data: AuthErrorBody | undefined = error?.response?.data;
      const code = data?.code;
      const backendMessage = data?.message;

      if (status === 403 && code === "EMAIL_NOT_VERIFIED") {
        toast({
          title: "Email not verified",
          description:
            "We sent a verification link to your inbox. Please verify your email before signing in.",
          variant: "destructive",
        });
      } else if (status === 429) {
        toast({
          title: "Sign in failed",
          description: backendMessage || "Too many login attempts. Please wait a few minutes and try again.",
          variant: "destructive",
        });
      } else if (status === 403 && code === "ACCOUNT_LOCKED") {
        const lock = getAccountLock(error);
        if (lock) {
          // Persistent live countdown + disabled button replaces a one-shot toast.
          setLockUntil(lock.unlockAt);
        } else {
          toast({
            title: "Sign in failed",
            description:
              backendMessage || "Your account is temporarily locked after repeated failed login attempts.",
            variant: "destructive",
          });
        }
      } else {
        toast({
          title: "Sign in failed",
          description: backendMessage || "An error occurred during sign in.",
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (signupData.password !== signupData.confirmPassword) {
        toast({
          title: "Passwords don't match",
          description: "Please make sure both passwords are identical.",
          variant: "destructive"
        });
        setLoading(false);
        return;
      }

      const registrationData: CandidateRegistrationRequest = {
        email: signupData.email,
        password: signupData.password,
        confirmPassword: signupData.confirmPassword,
        firstName: signupData.firstName,
        lastName: signupData.lastName
      };

      const registrationResponse = await authApi.register(registrationData);
      const { data } = registrationResponse;

      if (isPendingVerification(data)) {
        toast({
          title: "Account created",
          description: "Check your email to verify your account.",
        });
        setVerifyEmail(data.email);
        setLoginData({ email: data.email, password: "" });
        setSignupData({
          firstName: "",
          lastName: "",
          email: "",
          password: "",
          confirmPassword: "",
        });
        setActiveTab("verify");
        return;
      }

      const role = getUserRole(data.tokens.accessToken) || data.user.role;

      login({
        id: data.user.id,
        email: data.user.email,
        firstName: data.user.firstName,
        lastName: data.user.lastName,
        role: role || undefined
      }, data.tokens.accessToken, data.tokens.refreshToken);

      toast({
        title: "Account created",
        description: "Welcome! Your account has been created successfully.",
      });

      navigate(redirectTo);
    } catch (error: any) {
      const status = error?.response?.status;
      const backendMessage = error?.response?.data?.message;

      let title = "Sign up failed";
      let description = backendMessage || "An error occurred during sign up.";

      if (!error?.response) {
        title = "Network error";
        description = "Could not reach the server. Check your connection and try again.";
      } else if (status === 409) {
        title = "Account already exists";
        description =
          backendMessage ||
          "An account with this email already exists. Please sign in instead.";
      } else if (status === 429) {
        title = "Too many attempts";
        description =
          backendMessage ||
          "You've tried to register too many times. Please wait a few minutes and try again.";
      } else if (status === 400 || status === 422) {
        title = "Please check your details";
        description = backendMessage || "Some fields are invalid. Please review and try again.";
      }

      toast({
        title,
        description,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await authApi.requestPasswordReset(forgotEmail);
      setForgotSent(true);
      toast({
        title: "Check your inbox",
        description:
          "If an account exists for that email, we've sent a link to reset your password.",
      });
    } catch (error: any) {
      const status = error?.response?.status;
      const backendMessage = error?.response?.data?.message;

      if (status === 403) {
        toast({
          title: "Password reset unavailable",
          description:
            backendMessage ||
            "This account uses social login, so a password reset isn't available.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Couldn't send reset link",
          description: backendMessage || "An error occurred. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const openForgotPassword = () => {
    setForgotEmail(loginData.email);
    setForgotSent(false);
    setActiveTab("forgot");
  };

  const backToLogin = () => {
    setActiveTab("login");
    setForgotSent(false);
  };

  const handleInputChange = (form: "login" | "signup", field: string, value: string) => {
    if (form === "login") {
      setLoginData(prev => ({ ...prev, [field]: value }));
    } else {
      setSignupData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleLogout = () => {
    logout();
    toast({
      title: "Signed out",
      description: "You've been successfully signed out.",
    });
    navigate('/auth');
  };

  if (isAuthenticated) {
    return (
      <AuthLayout
        panelHeading="Welcome back to AI2Aim"
        heading={`Welcome back, ${user?.firstName ?? ""}`.trim()}
        subheading={user?.email ?? ""}
      >
        <div className="space-y-6">
          <div
            className="flex items-center gap-3 rounded-xl p-3"
            style={{
              background: "var(--landing-surface)",
              border: "1px solid var(--landing-glass-border)",
            }}
          >
            <div
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full"
              style={{
                background: "rgba(var(--acc2-rgb),0.1)",
                border: "1px solid rgba(var(--acc2-rgb),0.2)",
              }}
            >
              <User className="h-6 w-6 text-[var(--acc2)]" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold" style={{ color: "var(--landing-text)" }}>
                {user?.firstName} {user?.lastName}
              </p>
              <p className="truncate text-xs" style={{ color: "var(--landing-text-muted)" }}>
                {user?.email}
              </p>
            </div>
          </div>
          <Link
            to="/profile"
            className="btn-electric flex w-full items-center justify-center gap-2 rounded-lg px-6 py-3 font-semibold"
          >
            View saved jobs & applications
            <ArrowRight className="h-4 w-4" />
          </Link>
          <button
            type="button"
            onClick={handleLogout}
            className="flex w-full items-center justify-center gap-2 rounded-lg px-6 py-3 font-semibold transition-colors"
            style={surfaceStyle}
          >
            <LogOut className="h-4 w-4" />
            Sign Out
          </button>
        </div>
      </AuthLayout>
    );
  }

  const headings = {
    login: {
      panel: "Welcome back",
      h: "Sign in",
      sub: "Enter your credentials to access your account",
    },
    signup: {
      panel: "Get started",
      h: "Create account",
      sub: "Fill in your information to create an account",
    },
    forgot: {
      panel: "Reset access",
      h: "Reset password",
      sub: "Enter your email and we'll send you a link to reset your password",
    },
    verify: {
      panel: "Almost there",
      h: "Check your email",
      sub: "One quick step to activate your account.",
    },
  }[activeTab];

  return (
    <AuthLayout panelHeading={headings.panel} heading={headings.h} subheading={headings.sub}>
      {(activeTab === "login" || activeTab === "signup") && (
        <div
          className="relative mb-6 rounded-lg p-1"
          style={{
            background: "var(--landing-surface)",
            border: "1px solid var(--landing-glass-border)",
          }}
        >
          <div
            className="absolute top-1 h-[calc(100%-8px)] rounded-md transition-all duration-300 ease-expo-out"
            style={{
              left: activeTab === "login" ? "4px" : "calc(50% + 2px)",
              width: "calc(50% - 4px)",
              background: "var(--acc2)",
              boxShadow: "0 4px 16px rgba(var(--acc2-rgb),0.3)",
            }}
          />
          <div className="relative flex">
            {(["login", "signup"] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className="relative z-10 flex-1 rounded-md px-4 py-2.5 text-sm font-semibold transition-colors duration-200"
                style={{
                  color: activeTab === tab ? "var(--btnfg)" : "var(--landing-text-muted)",
                }}
              >
                {tab === "login" ? "Sign in" : "Sign up"}
              </button>
            ))}
          </div>
        </div>
      )}

      {activeTab === "verify" ? (
        <div className="space-y-6">
          <div className="flex justify-center">
            <div
              className="flex h-16 w-16 items-center justify-center rounded-full"
              style={{
                background: "rgba(var(--acc2-rgb),0.1)",
                border: "1px solid rgba(var(--acc2-rgb),0.2)",
              }}
            >
              <MailCheck className="h-8 w-8 text-[var(--acc2)]" />
            </div>
          </div>
          <div
            className="rounded-lg p-4"
            style={{
              background: "var(--landing-surface)",
              border: "1px solid var(--landing-glass-border)",
            }}
          >
            <p className="text-sm leading-relaxed" style={{ color: "var(--landing-text)" }}>
              We sent a verification link to{" "}
              <span className="font-semibold text-[var(--acc2)]">{verifyEmail}</span>. Click the link
              in that email to activate your account, then sign in to continue.
            </p>
          </div>
          <p className="text-xs leading-relaxed" style={{ color: "var(--landing-text-muted)" }}>
            Didn't get it? Check your spam folder. The link can take a minute to arrive.
          </p>
          <button
            type="button"
            onClick={backToLogin}
            className="w-full rounded-lg px-6 py-3 font-semibold transition-colors"
            style={surfaceStyle}
          >
            Back to sign in
          </button>
        </div>
      ) : activeTab === "forgot" ? (
        forgotSent ? (
          <div className="space-y-5">
            <div
              className="rounded-lg p-4"
              style={{
                background: "var(--landing-surface)",
                border: "1px solid var(--landing-glass-border)",
              }}
            >
              <p className="text-sm" style={{ color: "var(--landing-text)" }}>
                If an account exists for{" "}
                <span className="font-medium">{forgotEmail}</span>, a password reset link is on
                its way. The link is valid for 1 hour.
              </p>
            </div>
            <button
              type="button"
              onClick={backToLogin}
              className="w-full rounded-lg px-6 py-3 font-semibold transition-colors"
              style={surfaceStyle}
            >
              Back to sign in
            </button>
          </div>
        ) : (
          <form onSubmit={handleForgotPassword} className="space-y-5">
            <AuthInput
              id="forgot-email"
              label="Email"
              icon={Mail}
              type="email"
              value={forgotEmail}
              onChange={setForgotEmail}
              placeholder="you@example.com"
              required
              autoComplete="email"
            />
            <div className="space-y-3 pt-1">
              <AuthSubmitButton loading={loading} loadingText="Sending…">
                Send reset link
              </AuthSubmitButton>
              <button
                type="button"
                onClick={backToLogin}
                className="w-full text-center text-sm transition-colors hover:text-[var(--acc2)]"
                style={{ color: "var(--landing-text-muted)" }}
              >
                Back to sign in
              </button>
            </div>
          </form>
        )
      ) : activeTab === "login" ? (
        <form onSubmit={handleLogin} className="space-y-5">
          {isLocked && (
            <div
              className="rounded-xl p-4"
              style={{
                background: "rgba(245, 158, 11, 0.08)",
                border: "1px solid rgba(245, 158, 11, 0.3)",
              }}
            >
              <div className="flex items-start gap-3">
                <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
                <div className="space-y-1.5">
                  <p className="text-sm font-semibold text-amber-300">
                    Account temporarily locked
                  </p>
                  <p className="text-xs leading-relaxed text-amber-200/70">
                    Too many failed sign-in attempts. For your security, sign-in is paused.
                  </p>
                  <p className="flex items-center gap-1.5 text-sm font-semibold tabular-nums text-amber-200">
                    <Clock className="h-3.5 w-3.5" />
                    Try again in {formatLockCountdown(lockSecondsLeft)}
                  </p>
                  <button
                    type="button"
                    onClick={openForgotPassword}
                    className="text-xs font-semibold text-amber-300 underline underline-offset-2 transition-colors hover:text-amber-200"
                  >
                    Reset your password to unlock immediately
                  </button>
                </div>
              </div>
            </div>
          )}
          <AuthInput
            id="login-email"
            label="Email"
            icon={Mail}
            type="email"
            value={loginData.email}
            onChange={(v) => handleInputChange("login", "email", v)}
            placeholder="you@example.com"
            required
            autoComplete="email"
          />
          <AuthInput
            id="login-password"
            label="Password"
            icon={Lock}
            secret
            value={loginData.password}
            onChange={(v) => handleInputChange("login", "password", v)}
            placeholder="Enter your password"
            required
            autoComplete="current-password"
            autoFocus={prefilledEmail !== ""}
            action={
              <button
                type="button"
                onClick={openForgotPassword}
                className="text-xs font-medium transition-colors hover:text-[var(--acc2)]"
                style={{ color: "var(--landing-text-muted)" }}
              >
                Forgot password?
              </button>
            }
          />
          <div className="pt-1">
            <AuthSubmitButton
              loading={loading}
              disabled={isLocked}
              loadingText="Signing in…"
            >
              {isLocked ? `Try again in ${formatLockCountdown(lockSecondsLeft)}` : "Sign in"}
            </AuthSubmitButton>
          </div>
        </form>
      ) : (
        <form onSubmit={handleSignup} className="space-y-5">
          {/* One column below 640px: two icon inputs side by side left about
              100px of typing room each on a phone (T101026002). */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <AuthInput
              id="signup-firstName"
              label="First name"
              icon={User}
              value={signupData.firstName}
              onChange={(v) => handleInputChange("signup", "firstName", v)}
              placeholder="John"
              required
              autoComplete="given-name"
            />
            <AuthInput
              id="signup-lastName"
              label="Last name"
              icon={User}
              value={signupData.lastName}
              onChange={(v) => handleInputChange("signup", "lastName", v)}
              placeholder="Doe"
              required
              autoComplete="family-name"
            />
          </div>
          <AuthInput
            id="signup-email"
            label="Email"
            icon={Mail}
            type="email"
            value={signupData.email}
            onChange={(v) => handleInputChange("signup", "email", v)}
            placeholder="you@example.com"
            required
            autoComplete="email"
          />
          <AuthInput
            id="signup-password"
            label="Password"
            icon={Lock}
            secret
            value={signupData.password}
            onChange={(v) => handleInputChange("signup", "password", v)}
            placeholder="Create a password"
            required
            autoComplete="new-password"
          />
          <AuthInput
            id="signup-confirmPassword"
            label="Confirm password"
            icon={Lock}
            secret
            value={signupData.confirmPassword}
            onChange={(v) => handleInputChange("signup", "confirmPassword", v)}
            placeholder="Confirm your password"
            required
            autoComplete="new-password"
          />
          <div className="pt-1">
            <AuthSubmitButton loading={loading} loadingText="Creating account…">
              Create account
            </AuthSubmitButton>
          </div>
        </form>
      )}

      <div
        className="mt-6 pt-6"
        style={{ borderTop: "1px solid var(--landing-glass-border)" }}
      >
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm transition-colors hover:text-[var(--acc2)]"
          style={{ color: "var(--landing-text-muted)" }}
        >
          <ArrowLeft className="h-4 w-4" />
          Back to home
        </Link>
      </div>
    </AuthLayout>
  );
}
